import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Box, Button, Chip, CircularProgress, Link, Paper, Stack, Typography } from '@mui/material';
import { AuthService } from '../../services/auth.service';
import { MaintenanceDashboardService } from '../../services/maintenance.service';
import { MntManagerDashboard, mntLabel } from '../../interfaces/maintenance';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { WO_PRIORITY_COLOR } from '../maintenance/woCommon';
import { DashList, fmtMinutesShort, fmtShortDate, machineLabel } from '../maintenance/MntDashBits';
import MyBreakdownsDashSection from '../maintenance/MyBreakdownsDashSection';
import { DashEmpty, DashSection } from './personal/DashSection';
import StatTile from './charts/StatTile';
import HBars from './charts/HBars';
import Columns from './charts/Columns';
import UserDashboardView from './UserDashboardView';
import { useDashboardAccess } from './useDashboardAccess';

const fmtInt = (n: number) => Math.round(n).toLocaleString('en-US');

/**
 * Maintenance manager home: the whole institution's maintenance picture. KPI tile strip, four chart
 * sections, then the action lists (requests to triage, unassigned work, parts waits, overdue PM, work to
 * close, utility readings still missing today). Machine/person names only; no cost figure is read here.
 */
const MaintenanceDashboard: React.FC = () => {
    const { enabledModules, ready } = useDashboardAccess();
    const moduleOff = ready && !enabledModules.has('maintenance');
    const canTriage = usePermission(PERMISSIONS.MAINTENANCE_REQUEST_TRIAGE);
    const [data, setData] = useState<MntManagerDashboard | null>(null);
    const [failed, setFailed] = useState(false);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId) { setFailed(true); setLoading(false); return; }
        try {
            setData(await MaintenanceDashboardService.manager(institutionId));
            setFailed(false);
        } catch {
            setFailed(true);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { if (!moduleOff) load(); }, [load, moduleOff]);

    // The institution switched maintenance off: this board has nothing to read, show the personal home.
    if (moduleOff) return <UserDashboardView />;

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
    }

    const k = data?.kpis;

    return (
        <Box data-help-page="maint.dash.page">
            <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" useFlexGap spacing={1} sx={{ mb: 2 }}>
                <Typography variant="h5" fontWeight="bold">Maintenance Dashboard</Typography>
                <Stack data-help-section="maint.dash.shortcuts" direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Button component={RouterLink} to="/maintenance/work-orders" size="small" variant="contained">Work orders</Button>
                    <Button component={RouterLink} to="/maintenance/requests" size="small" variant="outlined">Breakdown requests</Button>
                    <Button component={RouterLink} to="/maintenance/pm-due" size="small" variant="outlined">PM due</Button>
                    <Button component={RouterLink} to="/maintenance/equipment" size="small" variant="outlined">Equipment</Button>
                </Stack>
            </Stack>

            {failed && !data && <Alert severity="warning" sx={{ mb: 2 }} data-help-section="maint.dash.notice">Maintenance figures are unavailable right now.</Alert>}

            {data && k && (
                <>
                    <Paper variant="outlined" sx={{ p: 1, mb: 2 }} data-help-section="maint.dash.kpis">
                        <Stack direction="row" useFlexGap sx={{ flexWrap: 'wrap' }} data-testid="mnt-kpi-strip">
                            <StatTile data-help="maint.dash.tile.machinesDown" label="Machines down now" value={fmtInt(k.machinesDownNow)} tone={k.machinesDownNow > 0 ? 'critical' : 'good'} />
                            <StatTile data-help="maint.dash.tile.openBreakdowns" label="Open breakdowns" value={fmtInt(k.openBreakdowns)} tone={k.openBreakdowns > 0 ? 'warning' : 'default'} />
                            <StatTile data-help="maint.dash.tile.newRequests" label="New requests" value={fmtInt(k.newRequests)} sub="Waiting for triage" tone={k.newRequests > 0 ? 'warning' : 'default'} />
                            <StatTile data-help="maint.dash.tile.pmOverdue" label="PM overdue" value={fmtInt(k.pmOverdue)} tone={k.pmOverdue > 0 ? 'critical' : 'good'} />
                            <StatTile data-help="maint.dash.tile.pmDue7" label="PM due in 7 days" value={fmtInt(k.pmDue7Days)} />
                            <StatTile data-help="maint.dash.tile.downtimeToday" label="Downtime today" value={fmtMinutesShort(k.downtimeTodayMinutes)} sub={`${k.downtimeMtdHours.toFixed(1)} h this month`} />
                            <StatTile data-help="maint.dash.tile.mttr" label="MTTR this month" value={k.mttrMtdMinutes === null ? '-' : fmtMinutesShort(Math.round(k.mttrMtdMinutes))} sub="Mean repair time" />
                        </Stack>
                    </Paper>

                    <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, mb: 2 }}>
                        <DashSection title="Downtime by line" subtitle="Last 30 days, hours" data-help-section="maint.dash.downtimeLine">
                            {data.downtimeByLine30d.length === 0
                                ? <DashEmpty text="No downtime recorded in the last 30 days." />
                                : <HBars data={data.downtimeByLine30d} colorIndex={2} vFmt={n => `${n.toFixed(1)} h`} />}
                        </DashSection>
                        <DashSection title="Breakdowns by cause" subtitle="This month" data-help-section="maint.dash.causes">
                            {data.breakdownsByCauseMtd.length === 0
                                ? <DashEmpty text="No breakdowns this month." />
                                : <HBars data={data.breakdownsByCauseMtd} colorIndex={4} />}
                        </DashSection>
                        <DashSection title="Work orders opened and closed" subtitle="Last 30 days, per day" data-help-section="maint.dash.opened">
                            <Typography variant="caption" color="text.secondary">Opened</Typography>
                            <Columns data={data.workOrdersOpenedVsClosed30d.map(d => ({ label: d.date, value: d.opened }))} height={120} colorIndex={0} />
                            <Typography variant="caption" color="text.secondary">Closed</Typography>
                            <Columns data={data.workOrdersOpenedVsClosed30d.map(d => ({ label: d.date, value: d.closed }))} height={120} colorIndex={3} />
                        </DashSection>
                        <DashSection title="PM compliance" subtitle="Preventive work done on or before its due date, last 6 months" data-help-section="maint.dash.pmCompliance">
                            {data.pmCompliance6m.every(m => m.due === 0)
                                ? <DashEmpty text="No preventive work was due in the last 6 months." />
                                : <Columns
                                    data={data.pmCompliance6m.map(m => ({ label: m.month, value: m.percent ?? 0 }))}
                                    height={200} colorIndex={1} max={100} yFmt={n => `${Math.round(n)}%`} />}
                        </DashSection>
                    </Box>

                    <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' } }}>
                        <DashSection title={`New requests (${data.newRequests.total})`} subtitle="Oldest first. Open the inbox to accept, reject or merge." data-help-section="maint.dash.newRequests">
                            <DashList block={data.newRequests} empty="No requests waiting." testId="mnt-new-requests" keyOf={r => r.id}
                                render={r => (
                                    <>
                                        {canTriage
                                            ? <Link component={RouterLink} to="/maintenance/requests" underline="hover" sx={{ fontWeight: 600 }}>{r.requestNo}</Link>
                                            : <Typography variant="body2" sx={{ fontWeight: 600 }}>{r.requestNo}</Typography>}
                                        <Typography variant="body2" color="text.secondary">{machineLabel(r.equipmentCode, r.equipmentName, r.locationName)}</Typography>
                                        <Box sx={{ flex: 1 }} />
                                        {r.machineStopped && <Chip size="small" color="error" label="Stopped" />}
                                        {r.possibleDuplicate && <Chip size="small" color="warning" label="Possible duplicate" />}
                                        <Typography variant="caption" color="text.secondary">{fmtShortDate(r.createdAt)}</Typography>
                                    </>
                                )} />
                            {canTriage && data.newRequests.total > 0 && (
                                <Button component={RouterLink} to="/maintenance/requests" size="small" sx={{ mt: 1 }}>Triage requests</Button>
                            )}
                        </DashSection>
                        <DashSection title={`Unassigned work orders (${data.unassignedWorkOrders.total})`} subtitle="Open work with nobody on it" data-help-section="maint.dash.unassigned">
                            <DashList block={data.unassignedWorkOrders} empty="Every open work order has an owner." testId="mnt-unassigned" keyOf={r => r.id}
                                render={r => (
                                    <>
                                        <Link component={RouterLink} to={`/maintenance/work-orders/${r.id}`} underline="hover" sx={{ fontWeight: 600 }}>{r.woNo}</Link>
                                        <Typography variant="body2" color="text.secondary">{r.title}</Typography>
                                        <Box sx={{ flex: 1 }} />
                                        <Chip size="small" label={mntLabel(r.priority)} color={WO_PRIORITY_COLOR[r.priority as keyof typeof WO_PRIORITY_COLOR] ?? 'default'} />
                                    </>
                                )} />
                        </DashSection>
                        <DashSection title={`Waiting for parts (${data.waitingParts.total})`} subtitle="On hold for spares, with the linked requisition" data-help-section="maint.dash.waitingParts">
                            <DashList block={data.waitingParts} empty="Nothing is waiting for parts." testId="mnt-waiting-parts" keyOf={r => r.id}
                                render={r => (
                                    <>
                                        <Link component={RouterLink} to={`/maintenance/work-orders/${r.id}`} underline="hover" sx={{ fontWeight: 600 }}>{r.woNo}</Link>
                                        <Typography variant="body2" color="text.secondary">{machineLabel(r.equipmentCode, r.equipmentName)}</Typography>
                                        <Box sx={{ flex: 1 }} />
                                        <Typography variant="caption" color="text.secondary">
                                            {r.requisitionRefNo ? `${r.requisitionRefNo}${r.requisitionStatus ? ` · ${mntLabel(r.requisitionStatus)}` : ''}` : 'No requisition yet'}
                                        </Typography>
                                    </>
                                )} />
                        </DashSection>
                        <DashSection title={`Overdue PM (${data.overduePm.total})`} subtitle="Preventive plans past their due date" data-help-section="maint.dash.overduePm">
                            <DashList block={data.overduePm} empty="No preventive maintenance is overdue." testId="mnt-overdue-pm" keyOf={r => r.planId}
                                render={r => (
                                    <>
                                        <Link component={RouterLink} to={`/maintenance/pm-plans/${r.planId}`} underline="hover" sx={{ fontWeight: 600 }}>{r.planName}</Link>
                                        <Typography variant="body2" color="text.secondary">{machineLabel(r.equipmentCode, r.equipmentName, r.locationName)}</Typography>
                                        <Box sx={{ flex: 1 }} />
                                        <Chip size="small" color="error" label={r.daysUntilDue !== null && r.daysUntilDue !== undefined ? `${Math.abs(r.daysUntilDue)} d late` : 'Overdue'} />
                                    </>
                                )} />
                        </DashSection>
                        <DashSection title={`Completed, awaiting close (${data.awaitingClose.total})`} subtitle="Done by the technician, not yet signed off" data-help-section="maint.dash.awaitingClose">
                            <DashList block={data.awaitingClose} empty="No work is waiting to be closed." testId="mnt-awaiting-close" keyOf={r => r.id}
                                render={r => (
                                    <>
                                        <Link component={RouterLink} to={`/maintenance/work-orders/${r.id}`} underline="hover" sx={{ fontWeight: 600 }}>{r.woNo}</Link>
                                        <Typography variant="body2" color="text.secondary">{r.title}</Typography>
                                        <Box sx={{ flex: 1 }} />
                                        <Typography variant="caption" color="text.secondary">{r.assignedToName ?? ''} {fmtShortDate(r.completedAt)}</Typography>
                                    </>
                                )} />
                        </DashSection>
                        <DashSection title={`Utility readings missing today (${data.readingsMissingToday.total})`} subtitle="Meters with no reading yet today" data-help-section="maint.dash.readingsMissing">
                            <DashList block={data.readingsMissingToday} empty="All utility meters have a reading today." testId="mnt-readings-missing" keyOf={r => r.meterId}
                                render={r => (
                                    <>
                                        <Link component={RouterLink} to="/maintenance/readings" underline="hover" sx={{ fontWeight: 600 }}>{r.meterName || r.meterCode}</Link>
                                        <Typography variant="body2" color="text.secondary">{machineLabel(r.equipmentCode, r.equipmentName, r.locationName)}</Typography>
                                        <Box sx={{ flex: 1 }} />
                                        {r.unit && <Typography variant="caption" color="text.secondary">{r.unit}</Typography>}
                                    </>
                                )} />
                        </DashSection>
                    </Box>
                </>
            )}

            <MyBreakdownsDashSection />
        </Box>
    );
};

export default MaintenanceDashboard;
